import type { ContractIdData } from '@src/generic-components/ContractIdSelectionModal/types/ContractIdModal.types';
import { APP_EVENTS, APP_SESSION_KEYS } from '@src/constants/app-shell-constants';

export interface ContractIdOption {
  title: string;
  value: string;
  selected: boolean;
}

export interface ContractIdFilterConfig {
  contractId: {
    title: string;
    type: 'DynamicSearch' | 'Search';
    selectedValue: ContractIdOption;
    width: string;
    filterOptionsSize: 'small' | 'large';
    filterDisplayKey: string;
    filterOptionType: number;
    filterValueKey: string;
    sliceArray: boolean;
    filterKey: string;
    disabled: boolean;
    options: ContractIdOption[];
    activeFilter: boolean;
    disableSC: boolean;
    resetFilter: boolean;
    showFilter: boolean;
    filterLabel: string;
    dropdownNote: string;
    searchType: number;
  };
}

const parseContractIdValues = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseContractIdValues(item));
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);
    if (Array.isArray(parsedValue)) {
      return parseContractIdValues(parsedValue);
    }
  } catch {
    // Treat a non-JSON value as a comma-separated or single Contract ID.
  }

  return trimmedValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

export const normalizeContractIdValues = (value: unknown): string[] => {
  return [...new Set(parseContractIdValues(value))];
};

export const getContractIdDetailsFromSession = (
  storage: Storage = sessionStorage
): ContractIdData[] => {
  try {
    const details = JSON.parse(storage.getItem(APP_SESSION_KEYS.CONTRACT_ID_DETAILS) || 'null');
    if (!Array.isArray(details?.individual)) {
      return [];
    }

    return details.individual
      .map(
        (item: {
          ContractId?: unknown;
          contractId?: unknown;
          value?: unknown;
          title?: unknown;
          TinCount?: unknown;
          taxIdCount?: unknown;
        }) => {
          const contractId = item.ContractId ?? item.contractId ?? item.value ?? item.title;
          if (typeof contractId !== 'string' || !contractId.trim()) {
            return null;
          }

          const taxIdCount = Number(item.TinCount ?? item.taxIdCount ?? 0);
          return {
            contractId: contractId.trim(),
            taxIdCount: Number.isFinite(taxIdCount) ? taxIdCount : 0,
            isSelected: false,
            isDisabled: false,
          };
        }
      )
      .filter((item: ContractIdData | null): item is ContractIdData => item !== null);
  } catch {
    return [];
  }
};

export const serializeContractIdValues = (value: unknown): string => {
  const contractIds = normalizeContractIdValues(value);
  if (contractIds.length <= 1) return contractIds[0] || '';
  return JSON.stringify(contractIds);
};

export const getContractIdValuesFromSession = (storage: Storage = sessionStorage): string[] => {
  return normalizeContractIdValues(storage.getItem(APP_SESSION_KEYS.SELECTED_CONTRACT_ID));
};

export const getAvailableContractIdValuesFromSession = (
  storage: Storage = sessionStorage
): string[] => {
  const contractIdsFromDetails = getContractIdDetailsFromSession(storage).map(
    (contract) => contract.contractId
  );
  if (contractIdsFromDetails.length > 0) {
    return normalizeContractIdValues(contractIdsFromDetails);
  }

  return getContractIdValuesFromSession(storage);
};

// Consumers that read Contract IDs from session listen for this to refetch.
export const CONTRACT_ID_SCOPE_CHANGED_EVENT = APP_EVENTS.CONTRACT_ID_SCOPE_CHANGED;

export const getActiveContractIdFromSession = (storage: Storage = sessionStorage): string => {
  return storage.getItem(APP_SESSION_KEYS.ACTIVE_CONTRACT_ID) || '';
};

export const isHighVolumeContractIdFlow = (storage: Storage = sessionStorage): boolean => {
  return storage.getItem(APP_SESSION_KEYS.HI_VOLUME_CONTRACT_FLOW) === 'true';
};

export const isSingleContractIdFlow = (storage: Storage = sessionStorage): boolean => {
  return (
    storage.getItem(APP_SESSION_KEYS.CONTRACT_ID_NAVIGATION) === 'true' &&
    getAvailableContractIdValuesFromSession(storage).length === 1
  );
};

// Chip focus is stored separately so the user's Contract ID selection is never overwritten.
export const setActiveContractIdInSession = (
  contractId: string | null | undefined,
  storage: Storage = sessionStorage
): void => {
  if (contractId) {
    storage.setItem(APP_SESSION_KEYS.ACTIVE_CONTRACT_ID, contractId);
  } else {
    storage.removeItem(APP_SESSION_KEYS.ACTIVE_CONTRACT_ID);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(CONTRACT_ID_SCOPE_CHANGED_EVENT, {
        detail: { activeContractId: contractId || null },
      })
    );
  }
};

export const replaceActiveContractIdInSession = (
  contractId: string | null | undefined,
  storage: Storage = sessionStorage
): void => {
  storage.removeItem(APP_SESSION_KEYS.ACTIVE_CONTRACT_ID);
  if (contractId) {
    storage.setItem(APP_SESSION_KEYS.ACTIVE_CONTRACT_ID, contractId);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(CONTRACT_ID_SCOPE_CHANGED_EVENT, {
        detail: { activeContractId: contractId || null },
      })
    );
  }
};

export const getEffectiveContractIdValuesFromSession = (
  storage: Storage = sessionStorage
): string[] => {
  const selectedContractIds = getContractIdValuesFromSession(storage);
  const effectiveContractIds = selectedContractIds.length
    ? selectedContractIds
    : getAvailableContractIdValuesFromSession(storage);

  const activeContractId = getActiveContractIdFromSession(storage);
  return activeContractId && effectiveContractIds.includes(activeContractId)
    ? [activeContractId]
    : effectiveContractIds;
};

interface ContractIdSelectionSource {
  contractid?: unknown;
  activeOption?: unknown;
}

const getOptionValue = (option: unknown): unknown => {
  if (!option || typeof option !== 'object') return option;
  const { value, title } = option as { value?: unknown; title?: unknown };
  return value ?? title;
};

// Narrows the Contract ID payload scope to the active chip when one is selected.
export const resolveContractIdSelection = (filterData?: ContractIdSelectionSource): unknown => {
  const selectedOptions = Array.isArray(filterData?.contractid) ? filterData.contractid : [];
  const activeValue = getOptionValue(filterData?.activeOption);
  if (activeValue === undefined || activeValue === null) return filterData?.contractid;

  const activeSelection = selectedOptions.find((option) => getOptionValue(option) === activeValue);
  return activeSelection ? [activeSelection] : filterData?.contractid;
};

export const applyAppealsContractIdPayloadContext = <T extends Record<string, unknown>>(
  payload: T,
  storage: Storage = sessionStorage
): T & { ContractIds?: string[] } => {
  if (storage.getItem('contractIdNavigation') !== 'true') {
    return { ...payload };
  }

  const contractIds = getEffectiveContractIdValuesFromSession(storage);
  return contractIds.length ? { ...payload, ContractIds: contractIds } : { ...payload };
};

export const applyContractIdPayloadContext = <T extends Record<string, unknown>>(
  payload: T,
  selectedValues?: unknown,
  storage: Storage = sessionStorage
): T & { ContractIds?: string[] } => {
  const navigationActive = storage.getItem('contractIdNavigation') === 'true';
  if (!navigationActive) {
    return { ...payload };
  }

  let selectedContractIdValues: unknown;
  if (Array.isArray(selectedValues)) {
    selectedContractIdValues = selectedValues.map((item) => {
      if (!item || typeof item !== 'object') return item;
      const option = item as { value?: unknown; title?: unknown };
      return option.value ?? option.title;
    });
  } else if (selectedValues && typeof selectedValues === 'object') {
    const option = selectedValues as { value?: unknown; title?: unknown };
    selectedContractIdValues = option.value ?? option.title;
  } else {
    selectedContractIdValues = selectedValues;
  }
  const explicitContractIds = normalizeContractIdValues(selectedContractIdValues);
  const contractIds =
    explicitContractIds.length > 0 ? explicitContractIds : getContractIdValuesFromSession(storage);
  if (contractIds.length === 0) {
    return { ...payload };
  }

  const contractPayload = { ...payload } as T & {
    Tin?: unknown;
    Tins?: unknown;
    ProviderSysKey?: unknown;
  };
  delete contractPayload.Tin;
  delete contractPayload.Tins;
  delete contractPayload.ProviderSysKey;
  const output = contractIds.length
    ? { ...contractPayload, ContractIds: contractIds }
    : { ...contractPayload };
  return output as T & { ContractIds?: string[] };
};

export const createContractIdFilterConfig = (
  values: unknown,
  selectedValue?: string | string[],
  singleSelect = false
): ContractIdFilterConfig => {
  const normalizedValues = normalizeContractIdValues(values);
  const selectedValues = normalizeContractIdValues(selectedValue ?? normalizedValues[0] ?? '');
  const options = normalizedValues.map((value) => ({
    title: value,
    value,
    selected: selectedValues.includes(value),
  }));
  const activeValues = options.filter((option) => option.selected);
  const selectedOption =
    activeValues.length > 1
      ? {
          title: `${activeValues.length} Contract IDs Selected`,
          value: `${activeValues.length} Contract IDs Selected`,
          selected: true,
        }
      : activeValues[0] || {
          title: '',
          value: '',
          selected: false,
        };

  return {
    contractId: {
      title: 'Contract ID',
      type: singleSelect ? 'Search' : 'DynamicSearch',
      selectedValue: selectedOption,
      width: '195px',
      filterOptionsSize: 'large',
      filterDisplayKey: 'title',
      filterOptionType: 2,
      filterValueKey: 'value',
      sliceArray: true,
      filterKey: 'contractId',
      disabled: normalizedValues.length <= 1,
      options,
      activeFilter: normalizedValues.length > 0,
      disableSC: false,
      resetFilter: true,
      showFilter: true,
      filterLabel: 'Contract ID',
      dropdownNote: 'Contract ID',
      searchType: 2,
    },
  };
};
